import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { formatCurrency } from '../../lib/format';
import type { Trade } from '../../types';
import type { Position } from '../../lib/investments';

/** Hue única para magnitude (escala sequencial de um só tom). */
const BAR_HUE = '#2B6CB0';
const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/** Acima de 8 categorias a cauda vira "Outros" em vez de gerar mais cores. */
const MAX_BARS = 8;

/**
 * Títulos do Tesouro usam o nome como identificador e estouram o eixo
 * ("RENDA+ APOSENTADORIA EXTRA 2065"). O rótulo encurta; o nome inteiro
 * continua no tooltip.
 */
const MAX_LABEL = 14;

function shortenLabel(label: string): string {
  return label.length <= MAX_LABEL ? label : `${label.slice(0, MAX_LABEL - 1)}…`;
}

function compactBrl(value: number): string {
  if (Math.abs(value) >= 1000) {
    const thousands = value / 1000;
    return `R$${thousands.toFixed(thousands % 1 === 0 ? 0 : 1)}k`;
  }
  return `R$${value.toFixed(0)}`;
}

interface TooltipPayloadItem {
  payload: { label: string; value: number; hint?: string };
}

function ChartTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}) {
  if (!active || !payload?.length) return null;
  const item = payload[0]!.payload;
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2 shadow-md">
      <p className="text-xs font-medium text-foreground">{item.label}</p>
      <p className="text-sm font-semibold text-foreground">
        R$ {formatCurrency(item.value)}
      </p>
      {item.hint && <p className="text-xs text-muted-foreground">{item.hint}</p>}
    </div>
  );
}

export interface AllocationSlice {
  key: string;
  label: string;
  color: string;
  value: number;
}

export interface AllocationByTypeProps {
  slices: AllocationSlice[];
}

/**
 * Parte-do-todo com poucas classes: barra empilhada horizontal. Pizza de
 * três ou quatro fatias é justamente o que não se deve fazer aqui.
 */
export function AllocationByType({ slices }: AllocationByTypeProps) {
  const visible = slices.filter((slice) => slice.value > 0);
  const total = visible.reduce((sum, slice) => sum + slice.value, 0);

  if (total <= 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex h-7 w-full gap-[2px] overflow-hidden rounded-md">
        {visible.map((slice) => {
          const share = (slice.value / total) * 100;
          return (
            <div
              key={slice.key}
              className="h-full first:rounded-l-md last:rounded-r-md"
              style={{ width: `${share}%`, backgroundColor: slice.color }}
              title={`${slice.label}: ${share.toFixed(1)}%`}
            />
          );
        })}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {visible.map((slice) => (
          <div key={slice.key} className="flex items-center gap-2">
            <span
              className="size-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: slice.color }}
              aria-hidden
            />
            <span className="text-xs text-foreground-subtle">{slice.label}</span>
            <span className="text-xs font-semibold text-foreground">
              {((slice.value / total) * 100).toFixed(1)}%
            </span>
            <span className="text-xs text-muted-foreground">
              R$ {formatCurrency(slice.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export interface AllocationByAssetProps {
  positions: Position[];
}

/** Magnitude por papel: barras horizontais ordenadas, uma hue só. */
export function AllocationByAsset({ positions }: AllocationByAssetProps) {
  const data = useMemo(() => {
    const rows = positions
      .map((p) => ({
        label: p.asset.ticker,
        value: p.marketValue ?? p.totalCost,
        hint: `${p.quantity} x R$ ${formatCurrency(p.currentPrice ?? p.averageCost)}`,
      }))
      .sort((a, b) => b.value - a.value);

    if (rows.length <= MAX_BARS) return rows;

    const head = rows.slice(0, MAX_BARS - 1);
    const tail = rows.slice(MAX_BARS - 1);
    return [
      ...head,
      {
        label: 'Outros',
        value: tail.reduce((sum, r) => sum + r.value, 0),
        hint: `${tail.length} papéis`,
      },
    ];
  }, [positions]);

  if (data.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height={Math.max(140, data.length * 34)}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 56, left: 0, bottom: 0 }}>
        <XAxis type="number" hide />
        <YAxis
          type="category"
          dataKey="label"
          tick={{ fontSize: 11 }}
          tickFormatter={shortenLabel}
          tickLine={false}
          axisLine={false}
          width={96}
          interval={0}
        />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
        <Bar
          dataKey="value"
          fill={BAR_HUE}
          radius={[0, 4, 4, 0]}
          barSize={14}
          isAnimationActive={false}
        >
          <LabelList
            dataKey="value"
            position="right"
            formatter={(value: unknown) => compactBrl(Number(value) || 0)}
            style={{ fontSize: 11, fill: 'var(--color-foreground-subtle)' }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export interface EarningsByMonthProps {
  /** Valores líquidos recebidos por mês (índice 0 = janeiro). */
  monthlyTotals: number[];
  year: number;
}

export function EarningsByMonth({ monthlyTotals, year }: EarningsByMonthProps) {
  const data = monthlyTotals.map((value, index) => ({
    label: `${MONTH_LABELS[index]}/${String(year).slice(2)}`,
    value,
  }));

  const hasData = monthlyTotals.some((v) => v > 0);
  if (!hasData) return null;

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 0 }} barCategoryGap="25%">
        <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
        <YAxis
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          tickFormatter={compactBrl}
        />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
        <Bar dataKey="value" fill={BAR_HUE} radius={[4, 4, 0, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export interface ContributionHistoryProps {
  trades: Trade[];
}

/**
 * Aporte acumulado (custo), não patrimônio histórico: a brapi devolve só a
 * cotação de hoje, então valor de mercado passado não é calculável.
 */
export function ContributionHistory({ trades }: ContributionHistoryProps) {
  const data = useMemo(() => {
    if (trades.length === 0) return [];

    const byMonth = new Map<string, number>();
    for (const trade of trades) {
      const key = `${trade.date.getFullYear()}-${String(trade.date.getMonth() + 1).padStart(2, '0')}`;
      const signed =
        trade.operation === 'buy'
          ? trade.quantity * trade.unitPrice + trade.fees
          : -(trade.quantity * trade.unitPrice - trade.fees);
      byMonth.set(key, (byMonth.get(key) ?? 0) + signed);
    }

    let running = 0;
    return [...byMonth.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, delta]) => {
        running += delta;
        const [year, month] = key.split('-');
        return {
          label: `${MONTH_LABELS[Number(month) - 1]}/${year!.slice(2)}`,
          value: running,
        };
      });
  }, [trades]);

  if (data.length < 2) return null;

  return (
    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
        <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
        <YAxis
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          tickFormatter={compactBrl}
        />
        <Tooltip content={<ChartTooltip />} />
        <Line
          type="monotone"
          dataKey="value"
          stroke={BAR_HUE}
          strokeWidth={2}
          dot={{ r: 3, strokeWidth: 0, fill: BAR_HUE }}
          activeDot={{ r: 5, stroke: 'var(--color-surface)', strokeWidth: 2 }}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export interface ChartCardProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

export function ChartCard({ title, description, children }: ChartCardProps) {
  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle className="text-sm font-medium text-foreground-subtle">
          {title}
        </CardTitle>
        {description && <CardDescription className="text-xs">{description}</CardDescription>}
      </CardHeader>
      {children}
    </Card>
  );
}
